import { DIFFERENT_VARIABLES_BRIDGE_NAME, OUTGOING_BRIDGE_KIND } from '../../constnats/outgoingBridge';
import { sameVariableBridgeNameSchema } from '../../schemas/outgoingBridge.schemas';
import type { OutgoingBridgeKindType, OutgoingBridgeType, OutgoingBridgesType } from '../../schemas/outgoingBridge.schemas';
import type { DescriptorType } from '../../types/descriptor.types';
import { getDescriptorSections } from './descriptorSections';

type ResolvedOutgoingBridgeType = OutgoingBridgeType & {
    kind: OutgoingBridgeKindType;
    endDescriptor: DescriptorType | undefined;
    validEndDescriptor: boolean;
};

export type DescriptorBridgesType = {
    outgoing: ResolvedOutgoingBridgeType[];
    incoming: DescriptorType[];
};

export type BridgeEndDescriptorOptionType = {
    descriptorId: string;
    descriptorLabel: string;
    invalid: boolean;
};

export function getOutgoingBridges(value?: OutgoingBridgesType | null): OutgoingBridgesType {
    return {
        sameVariable: value?.sameVariable ?? [],
        differentVariables: value?.differentVariables ?? [],
    };
}

export function getSameVariableBridgeName(bridges: readonly OutgoingBridgeType[]): string {
    const name = sameVariableBridgeNameSchema.safeParse(bridges[0]?.name.trim());
    return name.success && bridges.every((bridge) => bridge.name.trim() === name.data) ? name.data : '';
}

export function getOutgoingBridgesForEditing(value?: OutgoingBridgesType | null): OutgoingBridgesType {
    const bridges = getOutgoingBridges(value);
    const sharedName = getSameVariableBridgeName(bridges.sameVariable);
    return {
        sameVariable: bridges.sameVariable.map((bridge) => ({ ...bridge, name: sharedName })),
        differentVariables: bridges.differentVariables.map((bridge) => ({ ...bridge, name: DIFFERENT_VARIABLES_BRIDGE_NAME })),
    };
}

export function getBridgeDescriptorLabel(descriptor: Pick<DescriptorType, 'id' | 'label'>): string {
    return descriptor.label.trim() || descriptor.id;
}

function getSelectableSections(descriptors: readonly DescriptorType[]): DescriptorType[][] {
    return getDescriptorSections(descriptors).map((section) => section.filter((descriptor) =>
        descriptor.editable && descriptor.dynamicField,
    ));
}

export function getAvailableBridgeEndDescriptors(descriptors: readonly DescriptorType[], startDescriptor: DescriptorType): DescriptorType[] {
    const currentDescriptors = descriptors.map((descriptor) => descriptor.id === startDescriptor.id ? startDescriptor : descriptor);
    const section = getSelectableSections(currentDescriptors).find((members) => members.some((member) => member.id === startDescriptor.id));
    return section?.filter((descriptor) => descriptor.id !== startDescriptor.id) ?? [];
}

export function getDescriptorBridges(descriptors: readonly DescriptorType[]): Map<string, DescriptorBridgesType> {
    const descriptorsById = new Map(descriptors.map((descriptor) => [descriptor.id, descriptor]));
    const sectionByDescriptorId = new Map<string, DescriptorType[]>();
    for (const section of getSelectableSections(descriptors)) {
        for (const descriptor of section) {
            sectionByDescriptorId.set(descriptor.id, section);
        }
    }
    const bridgesByDescriptorId = new Map<string, DescriptorBridgesType>(descriptors.map((descriptor) => [
        descriptor.id, { outgoing: [], incoming: [] },
    ]));

    for (const startDescriptor of descriptors) {
        const descriptorBridges = bridgesByDescriptorId.get(startDescriptor.id)!;
        const outgoingBridges = getOutgoingBridges(startDescriptor.outgoingBridges);
        const startSection = sectionByDescriptorId.get(startDescriptor.id);
        const linkedEndDescriptorIds = new Set<string>();
        for (const kind of Object.values(OUTGOING_BRIDGE_KIND)) {
            for (const bridge of outgoingBridges[kind]) {
                const endDescriptor = descriptorsById.get(bridge.endId);
                descriptorBridges.outgoing.push({
                    ...bridge,
                    kind,
                    endDescriptor,
                    validEndDescriptor: Boolean(startSection && startDescriptor.id !== bridge.endId
                        && startSection === sectionByDescriptorId.get(bridge.endId)),
                });
                if (endDescriptor && !linkedEndDescriptorIds.has(endDescriptor.id)) {
                    bridgesByDescriptorId.get(endDescriptor.id)!.incoming.push(startDescriptor);
                    linkedEndDescriptorIds.add(endDescriptor.id);
                }
            }
        }
    }
    return bridgesByDescriptorId;
}

export function getBridgeEndDescriptorOptions(
    availableDescriptors: readonly DescriptorType[],
    existingBridges: readonly OutgoingBridgeType[],
    invalidSelectionDescriptorIds: readonly string[],
): BridgeEndDescriptorOptionType[] {
    const availableDescriptorsById = new Map(availableDescriptors.map((descriptor) => [descriptor.id, descriptor]));
    const invalidSelectionDescriptorIdSet = new Set(invalidSelectionDescriptorIds);
    const optionsByDescriptorId = new Map<string, BridgeEndDescriptorOptionType>();

    for (const descriptor of availableDescriptors) {
        if (!invalidSelectionDescriptorIdSet.has(descriptor.id)) {
            optionsByDescriptorId.set(descriptor.id, {
                descriptorId: descriptor.id, descriptorLabel: getBridgeDescriptorLabel(descriptor), invalid: false,
            });
        }
    }
    for (const bridge of existingBridges) {
        if (!optionsByDescriptorId.has(bridge.endId)) {
            const descriptor = availableDescriptorsById.get(bridge.endId);
            optionsByDescriptorId.set(bridge.endId, {
                descriptorId: bridge.endId,
                descriptorLabel: descriptor ? getBridgeDescriptorLabel(descriptor) : bridge.endId,
                invalid: true,
            });
        }
    }
    return [...optionsByDescriptorId.values()];
}

export function updateBridges(
    existingBridges: readonly OutgoingBridgeType[],
    selectedDescriptorIds: readonly string[],
    bridgeEndDescriptorOptions: readonly BridgeEndDescriptorOptionType[],
    name: string,
): OutgoingBridgeType[] {
    const existingBridgesByEndDescriptorId = new Map(existingBridges.map((bridge) => [bridge.endId, bridge]));
    const optionsByDescriptorId = new Map(bridgeEndDescriptorOptions.map((option) => [option.descriptorId, option]));
    return [...new Set(selectedDescriptorIds)].flatMap((descriptorId) => {
        const existingBridge = existingBridgesByEndDescriptorId.get(descriptorId);
        if (existingBridge) {
            return [{ ...existingBridge, name }];
        }
        const option = optionsByDescriptorId.get(descriptorId);
        return option && !option.invalid ? [{ endId: descriptorId, name }] : [];
    });
}
